import { useState, useEffect, useRef } from "react";
import { DEFAULT_SEARCH_DETAILS } from "../constants";
import { isResultNotAvailalable, processSearchData } from "../utils";
import { useSearchRequests } from "./useSearchRequests";
import { HeaderInfo } from "../types";
import useDebounce from "./useDebounce";
import { ROLES_ROUTE } from "../../../shared/constants/constant-data";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";

const contentNum = Math.ceil((window.innerHeight - 50) / 50);

export const useSearch = () => {
  const [searchText, setSearchText] = useState("");
  const [regexSearch, setRegexSearch] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [isApiStartedCalling, setIsApiStartedCalling] = useState(false);
  const [resultAvailable, setResultAvailable] = useState<boolean | null>(null);
  const [invalidRegex, setInvalidRegex] = useState(false);
  const [headersInfo, setHeadersInfo] = useState<HeaderInfo[]>(
    DEFAULT_SEARCH_DETAILS
  );
  const [pageNum, setPageNum] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [isScrollOn, setIsScrollOn] = useState(false);
  const [selected_tab, setSelectedTab] = useState<string>("");

  const debouncedText = useDebounce(searchText, 500);
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const { search, cancelAllRequests } = useSearchRequests();
  const { hasPermissions } = useRolePermissions();

  const managePermission = () => {
    const permissions = [
      { key: "Assets", role: ROLES_ROUTE.DEVICE_MGNT },
      { key: "Scans", role: ROLES_ROUTE.MYSCANS },
      { key: "Users", role: ROLES_ROUTE.USER_MGNT },
      { key: "Roles", role: ROLES_ROUTE.ROLE_MGNT },
      { key: "Controls", role: ROLES_ROUTE.SECURITY_INFO },
      { key: "Benchmarks", role: ROLES_ROUTE.SECURITY_INFO },
      { key: "Resources", role: ROLES_ROUTE.DEVICE_MGNT },
      { key: "Cloud-Controls", role: ROLES_ROUTE.SECURITY_INFO },
    ];

    const filteredDetails = DEFAULT_SEARCH_DETAILS.filter((item) => {
      const perm = permissions.find((p) => p.key === item.key);
      return !perm || hasPermissions(perm.role);
    });

    setHeadersInfo(filteredDetails);
  };

  useEffect(() => {
    managePermission();
    // eslint-disable-next-line
  }, []);

  const restrictApiCall = () => {
    setResultAvailable(null);
    setHeadersInfo((prev) =>
      prev.map((header) => ({
        ...header,
        value: 0,
        response: [],
        data: [],
        isApiCall: false,
      }))
    );
  };

  const afterSearch = (newHeadersInfo: HeaderInfo[]) => {
    const { dataAvailable, invalid_regex } =
      isResultNotAvailalable(newHeadersInfo);
    setInvalidRegex(invalid_regex);
    setResultAvailable(dataAvailable);
    setHeadersInfo(newHeadersInfo);
    setIsApiStartedCalling(false);
  };

  useEffect(() => {
    setSelectedTab("");
    // if ((regexSearch && selected_tab.length > 0) || selected_tab.length > 0) {
    //   fetchResultsForSelectedTab();
    // } else {
    searchContent();
    // }
  }, [debouncedText, regexSearch]);

  const searchContent = () => {
    if (!debouncedText || debouncedText.length < 3) {
      restrictApiCall();
      return;
    }

    try {
      if (regexSearch) {
        new RegExp(debouncedText);
      }
    } catch {
      setInvalidRegex(true);
      restrictApiCall();
      return;
    }

    cancelAllRequests();
    setIsApiStartedCalling(true);
    setHighlight(true);

    const newHeadersInfo = headersInfo.map((header) => ({ ...header }));
    let apiCounter = 0;

    newHeadersInfo.forEach((headerObj: HeaderInfo) => {
      headerObj.isApiCall = false;

      search(debouncedText, headerObj.key, regexSearch ? "regex" : "", 1, 5)
        .then((response) => {
          apiCounter++;

          if (
            response &&
            response.status === 200 &&
            response.data?.data?.length
          ) {
            headerObj.value = response.data?.count || 0;
            headerObj.response = response.data.data;
            headerObj.isApiCall = true;
            headerObj.highlight = false;
            headerObj = {
              ...headerObj,
              ...processSearchData(response.data.data, headerObj),
            };
          } else {
            headerObj.value = 0;
            headerObj.response =
              regexSearch && response.data === null ? null : [];
            headerObj.data = [];
            headerObj.isApiCall = true;
            headerObj.highlight = false;
          }

          if (apiCounter === newHeadersInfo.length) {
            afterSearch(newHeadersInfo);
          }
        })
        .catch((err) => {
          if (err.name === "CanceledError" || err.name === "AbortError") return;

          apiCounter++;
          headerObj.data = [];
          headerObj.value = 0;
          headerObj.isApiCall = true;

          if (apiCounter === newHeadersInfo.length) {
            afterSearch(newHeadersInfo);
          }
        });
    });
  };

  const fetchResultsForSelectedTab = (isScroll = false, nextPg?: number) => {
    const activeHeader = headersInfo.find(
      (header) => header.key === selected_tab
    );
    if (activeHeader) viewApiCall(activeHeader, isScroll, nextPg);
  };

  const onScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
    if (isAtBottom && !isApiStartedCalling && pageNum < numberOfPages) {
      console.log("Loading more results...");
      const nextPg = pageNum + 1;
      setPageNum(nextPg);
      setIsScrollOn(true);
      fetchResultsForSelectedTab(true, nextPg);
    }
  };

  const viewApiCall = (selectedTab: HeaderInfo, isScroll = false, nextPg?: number) => {
    if (!isScroll) {
      setPageNum(1);
      cancelAllRequests();
      setIsApiStartedCalling(true);
    }

    setHighlight(true);

    const searchTextValue = searchFieldRef.current?.value ?? "";

    search(
      searchTextValue,
      selectedTab.key,
      regexSearch ? "regex" : "",
      nextPg ?? 1,
      contentNum
    )
      .then((res) => {
        setNumberOfPages(Number(res.data.numberofpages));

        const processedHeader = processSearchData(
          res.data.data,
          selectedTab,
          isScroll
        );
        const processedHeaders = headersInfo.map((header) => {
          if (header.key === selectedTab.key) {
            return {
              ...header,
              ...processedHeader,
              highlight: true,
            };
          }
          return header;
        });

        afterSearch(processedHeaders);
      })
      .catch((err) => {
        console.error(err);
        setResultAvailable(false);
      })
      .finally(() => {
        setIsScrollOn(false);
        setIsApiStartedCalling(false);
      });
  };

  const onViewAllHandler = (selectedTab: HeaderInfo) => {
    setPageNum(1);
    setIsScrollOn(false);
    setSelectedTab(selectedTab.key);
    viewApiCall(selectedTab);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    if (newText === searchText) return;

    //resetting the tab whenever search text is changed
    setSelectedTab("");

    setSearchText(newText);
  };

  const handleClear = () => {
    setSearchText("");
    setHighlight(false);
    setIsApiStartedCalling(false);
    setResultAvailable(null);
    setRegexSearch(false);
    setInvalidRegex(false);
    setSelectedTab("");
    setPageNum(1);
    setNumberOfPages(1);
    if (searchFieldRef.current) searchFieldRef.current.value = "";
    cancelAllRequests();
    setHeadersInfo((prev) =>
      prev.map((header) => ({
        ...header,
        value: 0,
        response: [],
        data: [],
        isApiCall: false,
        highlight: false,
      }))
    );
  };

  const onTabSelection = (header: HeaderInfo) => {
    setHighlightHeader(header.key, true);
    setSelectedTab(header.key);
    setPageNum(1);
    setIsScrollOn(false);
    viewApiCall(header);
  };

  const onTabClear = (header: HeaderInfo) => {
    setHighlightHeader(header.key, false);
    setSelectedTab("");
    searchContent();
  };

  const setHighlightHeader = (key: string, shouldHighlight: boolean) => {
    setHeadersInfo((prev) =>
      prev.map((h) =>
        h.key === key
          ? { ...h, highlight: shouldHighlight }
          : { ...h, highlight: false }
      )
    );
  };

  return {
    searchText,
    setSearchText,
    regexSearch,
    setRegexSearch,
    highlight,
    isApiStartedCalling,
    resultAvailable,
    invalidRegex,
    headersInfo,
    searchFieldRef,
    handleSearchChange,
    handleClear,
    setHighlightHeader,
    onViewAllHandler,
    onScroll,
    isScrollOn,
    selected_tab,
    setSelectedTab,
    onTabClear,
    onTabSelection,
  };
};
